/**
 * Crossword generation from the learner's own vocabulary.
 *
 * The one game a Verbi reviewer named: *"I just did a crossword (that it generated based
 * on the new words it introduced to me)… Looks like an app that is ACTUALLY WORTH PAYING
 * FOR."* It has been promised in `01-information-architecture` and `04-screen-specs`
 * since the start and built nowhere.
 *
 * A crossword is the better game of the two we promise, and the reason is the clue: a
 * word search tests whether you can *spot* a word you are already looking at, while a
 * crossword tests whether you can *produce* it from its meaning. That is recall rather
 * than recognition, so it grades as the harder modality.
 *
 * Deterministic from a seed, like the word search, so the same learner gets the same
 * puzzle on every device and the output is testable.
 */

import { createRng, gridForm } from './wordsearch.js';

export type EntryDirection = 'across' | 'down';

export interface CrosswordWord {
  readonly itemId: string;
  /** The answer as written, accents and all. Folded for the grid, shown for the clue. */
  readonly text: string;
  /** What the learner reads. The gloss, normally. */
  readonly clue: string;
}

export interface CrosswordEntry {
  readonly itemId: string;
  readonly answer: string;
  readonly clue: string;
  readonly number: number;
  readonly direction: EntryDirection;
  readonly row: number;
  readonly col: number;
  readonly length: number;
}

export interface Crossword {
  /** `null` is a blocked square; a letter is part of at least one answer. */
  readonly grid: readonly (string | null)[][];
  readonly entries: readonly CrosswordEntry[];
  /** Words that could not be interlocked; surfaced rather than silently dropped. */
  readonly unplaced: readonly string[];
  readonly rows: number;
  readonly cols: number;
}

export interface CrosswordInput {
  readonly words: readonly CrosswordWord[];
  readonly seed: string;
  /** Hard cap on the board. A puzzle larger than this is unreadable on a phone. */
  readonly size?: number;
}

interface Placement {
  readonly word: string;
  readonly itemId: string;
  readonly clue: string;
  readonly row: number;
  readonly col: number;
  readonly direction: EntryDirection;
}

/**
 * Builds the puzzle.
 *
 * The algorithm is the standard one and the constraints are what make it a crossword
 * rather than a pile of words: every word after the first must cross one already on the
 * board, no two words may run alongside each other, and a crossing must agree on its
 * shared letter.
 */
/**
 * How many layouts to try before keeping one.
 *
 * The first layout a seed produces is often a poor one: on a thirteen-word set, seeds
 * observed placing eleven words and eight words respectively from the same input. The
 * learner cannot tell which seed they got, only that half their words are missing. Six
 * attempts is enough to make that rare and is still instant — the whole generation is
 * a few hundred microseconds.
 */
const LAYOUT_ATTEMPTS = 6;

/**
 * The best layout of several, chosen deterministically.
 *
 * Best means *most words placed*, ties broken by the tighter board, because a crossword
 * that uses more of the learner's words and wastes less space is better on both counts.
 * Still a pure function of the seed: the attempts come from derived seeds, so the same
 * learner gets the same puzzle.
 */
export function generateCrossword(input: CrosswordInput): Crossword {
  let best: Crossword | null = null;
  for (let attempt = 0; attempt < LAYOUT_ATTEMPTS; attempt++) {
    const candidate = layoutOnce({ ...input, seed: `${input.seed}#${attempt}` });
    if (best === null || isBetter(candidate, best)) best = candidate;
  }
  return best ?? layoutOnce(input);
}

function isBetter(candidate: Crossword, incumbent: Crossword): boolean {
  if (candidate.entries.length !== incumbent.entries.length) {
    return candidate.entries.length > incumbent.entries.length;
  }
  return candidate.rows * candidate.cols < incumbent.rows * incumbent.cols;
}

function layoutOnce(input: CrosswordInput): Crossword {
  const rng = createRng(input.seed);
  const size = input.size ?? 13;

  const prepared = input.words
    .map((word) => ({ ...word, grid: gridForm(word.text) }))
    .filter((word) => word.grid.length >= 3 && word.grid.length <= size)
    // Longest first: a long word gives later words more letters to cross.
    .sort((a, b) => b.grid.length - a.grid.length || a.itemId.localeCompare(b.itemId));

  if (prepared.length === 0) {
    return { grid: [], entries: [], unplaced: [], rows: 0, cols: 0 };
  }

  const cells = new Map<string, string>();
  const placements: Placement[] = [];
  const unplaced: string[] = [];

  const first = prepared[0];
  if (first === undefined) return { grid: [], entries: [], unplaced: [], rows: 0, cols: 0 };

  const mid = Math.floor(size / 2);
  write(cells, first.grid, mid, Math.max(0, mid - Math.floor(first.grid.length / 2)), 'across');
  placements.push({
    word: first.grid,
    itemId: first.itemId,
    clue: first.clue,
    row: mid,
    col: Math.max(0, mid - Math.floor(first.grid.length / 2)),
    direction: 'across',
  });

  for (const word of prepared.slice(1)) {
    const spot = findCrossing(cells, placements, word.grid, size, rng);
    if (spot === null) {
      unplaced.push(word.text);
      continue;
    }
    write(cells, word.grid, spot.row, spot.col, spot.direction);
    placements.push({
      word: word.grid,
      itemId: word.itemId,
      clue: word.clue,
      row: spot.row,
      col: spot.col,
      direction: spot.direction,
    });
  }

  return layout(cells, placements, prepared, unplaced);
}

function key(row: number, col: number): string {
  return `${row},${col}`;
}

function write(
  cells: Map<string, string>,
  word: string,
  row: number,
  col: number,
  direction: EntryDirection,
): void {
  for (let i = 0; i < word.length; i++) {
    const r = direction === 'down' ? row + i : row;
    const c = direction === 'across' ? col + i : col;
    cells.set(key(r, c), word[i] as string);
  }
}

interface Spot {
  readonly row: number;
  readonly col: number;
  readonly direction: EntryDirection;
}

/**
 * The first legal crossing, searched in a deterministic order jittered by the seed.
 *
 * Jittered rather than random: trying candidates in a fixed order produces the same
 * dense, boring cross every time, and trying them in a fully random order makes the
 * puzzle unreproducible. The seed rotates the starting point instead.
 */
function findCrossing(
  cells: Map<string, string>,
  placements: readonly Placement[],
  word: string,
  size: number,
  rng: () => number,
): Spot | null {
  // Three rotations, not one. Rotating only which placed word is tried first produces
  // the same layout for most seeds once the board has a handful of words on it — the
  // first legal crossing is usually found on the first word examined whichever one that
  // is. Rotating the letter positions as well is what actually moves the puzzle.
  const wordOffset = Math.floor(rng() * Math.max(1, placements.length));
  const crossOffset = Math.floor(rng() * 16);
  const letterOffset = Math.floor(rng() * 16);

  for (let p = 0; p < placements.length; p++) {
    const placed = placements[(p + wordOffset) % placements.length];
    if (placed === undefined) continue;
    const direction: EntryDirection = placed.direction === 'across' ? 'down' : 'across';

    for (let ii = 0; ii < placed.word.length; ii++) {
      const i = (ii + crossOffset) % placed.word.length;
      const letter = placed.word[i];
      for (let jj = 0; jj < word.length; jj++) {
        const j = (jj + letterOffset) % word.length;
        if (word[j] !== letter) continue;

        const crossRow = placed.direction === 'down' ? placed.row + i : placed.row;
        const crossCol = placed.direction === 'across' ? placed.col + i : placed.col;
        const row = direction === 'down' ? crossRow - j : crossRow;
        const col = direction === 'across' ? crossCol - j : crossCol;

        if (fits(cells, word, row, col, direction, size)) return { row, col, direction };
      }
    }
  }
  return null;
}

/**
 * Whether a word may sit here.
 *
 * The adjacency rules are the ones that separate a crossword from a word jumble: a
 * letter may only touch a letter it actually crosses, and the squares immediately before
 * and after an answer must be empty, or two answers would read as one longer word.
 */
function fits(
  cells: ReadonlyMap<string, string>,
  word: string,
  row: number,
  col: number,
  direction: EntryDirection,
  size: number,
): boolean {
  if (row < 0 || col < 0) return false;
  const endRow = direction === 'down' ? row + word.length - 1 : row;
  const endCol = direction === 'across' ? col + word.length - 1 : col;
  if (endRow >= size || endCol >= size) return false;

  // The square before the start and after the end must be empty.
  const beforeRow = direction === 'down' ? row - 1 : row;
  const beforeCol = direction === 'across' ? col - 1 : col;
  const afterRow = direction === 'down' ? endRow + 1 : row;
  const afterCol = direction === 'across' ? endCol + 1 : col;
  if (cells.has(key(beforeRow, beforeCol)) || cells.has(key(afterRow, afterCol))) return false;

  let crossings = 0;
  for (let i = 0; i < word.length; i++) {
    const r = direction === 'down' ? row + i : row;
    const c = direction === 'across' ? col + i : col;
    const existing = cells.get(key(r, c));

    if (existing !== undefined) {
      if (existing !== word[i]) return false;
      crossings++;
      continue;
    }

    // An empty square must not have neighbours on the sides this word does not run
    // along, or the new word sits flush against an existing one.
    const sideA = direction === 'across' ? key(r - 1, c) : key(r, c - 1);
    const sideB = direction === 'across' ? key(r + 1, c) : key(r, c + 1);
    if (cells.has(sideA) || cells.has(sideB)) return false;
  }

  return crossings > 0;
}

/** Crops to the used area and numbers the entries the way a crossword is numbered. */
function layout(
  cells: ReadonlyMap<string, string>,
  placements: readonly Placement[],
  prepared: readonly (CrosswordWord & { grid: string })[],
  unplaced: readonly string[],
): Crossword {
  const coords = [...cells.keys()].map((k) => k.split(',').map(Number) as [number, number]);
  const minRow = Math.min(...coords.map(([r]) => r));
  const maxRow = Math.max(...coords.map(([r]) => r));
  const minCol = Math.min(...coords.map(([, c]) => c));
  const maxCol = Math.max(...coords.map(([, c]) => c));

  const rows = maxRow - minRow + 1;
  const cols = maxCol - minCol + 1;
  const grid: (string | null)[][] = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => cells.get(key(r + minRow, c + minCol)) ?? null),
  );

  // Numbered in reading order, which is the convention every solver already knows.
  const numbered = new Map<string, number>();
  let next = 1;
  const shifted = placements.map((p) => ({ ...p, row: p.row - minRow, col: p.col - minCol }));
  const starts = [...shifted].sort((a, b) => a.row - b.row || a.col - b.col);
  for (const start of starts) {
    const at = key(start.row, start.col);
    if (!numbered.has(at)) numbered.set(at, next++);
  }

  const byItem = new Map(prepared.map((word) => [word.itemId, word]));
  const entries: CrosswordEntry[] = shifted.map((placement) => ({
    itemId: placement.itemId,
    // The answer as written, so the clue list can show accents the grid folds away.
    answer: byItem.get(placement.itemId)?.text ?? placement.word,
    clue: placement.clue,
    number: numbered.get(key(placement.row, placement.col)) ?? 0,
    direction: placement.direction,
    row: placement.row,
    col: placement.col,
    length: placement.word.length,
  }));

  entries.sort((a, b) => a.number - b.number || a.direction.localeCompare(b.direction));
  return { grid, entries, unplaced, rows, cols };
}

// ── Playing ───────────────────────────────────────────────────────────────────

export interface CrosswordState {
  /** What the learner has typed, by `row,col`. Empty squares are absent. */
  readonly letters: Readonly<Record<string, string>>;
  readonly startedAt: number;
}

export function startCrossword(now: number): CrosswordState {
  return { letters: {}, startedAt: now };
}

export function typeLetter(
  state: CrosswordState,
  row: number,
  col: number,
  letter: string,
): CrosswordState {
  const folded = gridForm(letter);
  const letters = { ...state.letters };
  // An empty or non-letter key clears the square rather than writing rubbish into it.
  if (folded.length === 0) delete letters[key(row, col)];
  else letters[key(row, col)] = folded[0] as string;
  return { ...state, letters };
}

/** The entries the learner has completed correctly. */
export function solvedEntries(puzzle: Crossword, state: CrosswordState): readonly CrosswordEntry[] {
  return puzzle.entries.filter((entry) => {
    for (let i = 0; i < entry.length; i++) {
      const r = entry.direction === 'down' ? entry.row + i : entry.row;
      const c = entry.direction === 'across' ? entry.col + i : entry.col;
      if (state.letters[key(r, c)] !== puzzle.grid[r]?.[c]) return false;
    }
    return true;
  });
}

export function isCrosswordComplete(puzzle: Crossword, state: CrosswordState): boolean {
  return solvedEntries(puzzle, state).length === puzzle.entries.length;
}

/**
 * One graded review per solved entry.
 *
 * `production`, not `recognition`: a crossword answer is produced from a clue and the
 * crossing letters, which is recall. Grading it as recognition would tell the scheduler
 * the learner did something easier than they did and bring the card back too soon.
 */
export interface CrosswordGrade {
  readonly cardId: string;
  readonly modality: 'production';
  readonly correct: true;
  readonly elapsedMs: number;
  readonly hintUsed: false;
  readonly reviewedAt: number;
}

export function crosswordGrades(
  puzzle: Crossword,
  state: CrosswordState,
  cardIdByItemId: Readonly<Record<string, string>>,
  now: number,
): readonly CrosswordGrade[] {
  const solved = solvedEntries(puzzle, state);
  const elapsed = Math.max(1, Math.round((now - state.startedAt) / Math.max(1, solved.length)));
  const grades: CrosswordGrade[] = [];
  for (const entry of solved) {
    const cardId = cardIdByItemId[entry.itemId];
    if (cardId === undefined) continue;
    grades.push({
      cardId,
      modality: 'production',
      correct: true,
      elapsedMs: elapsed,
      hintUsed: false,
      reviewedAt: now,
    });
  }
  return grades;
}
